import React, { useState, useEffect } from "react";
import Table from "react-bootstrap/Table";
import EditIcon from "../../../assets/images/edit.png";
import DeleteIcon from "../../../assets/images/delete.png";
import SearchBar from "../../common/components/SearchBar";
import { Button } from "../../common/Button";
import { Link, useNavigate } from "react-router-dom";
import { Skeleton, TableCell, Tooltip } from "@mui/material";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { failed, success } from "../../common/Toastify";
import { Spinner } from "react-bootstrap";
import { useQuestionQuery } from "../../../hooks/ReactQueryHooks/useQuestionQuery";
import moment from "moment";
import EditQuestion from "./EditQuestion";
import { deleteQuestion } from "../../api/Questionnaire";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { useSelector } from "react-redux";
import ViewIcon from "../../../assets/images/view.png";
import { useQuestionCategoryQuery } from "../../../hooks/ReactQueryHooks/useQuestionCategoryQuery";
import Select from "react-select";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";

function QuestionsList() {
  const { getConfirmation } = useConfirmDialog();
  const userOrg = useSelector((state) => state?.auth?.user?.["custom:orgId"]);
  const userType = useSelector((state) => state?.auth?.user?.["cognito:groups"]);
  const admin = userType?.includes("Super-Admin");

  // org dropdown state
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(userOrg);

  // table state
  const [programList, setProgramList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [questionDataItem, setQuestionDataItem] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [sortBy, setSortBy] = useState("AscName");
  const [modalShow, setModalShow] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  // pagination
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const totalRows = filterData.length;

  // load orgs for dropdown
  useOrgQuery({
    onSuccess: (data) => {
      setOrganizations(data?.map((org) => ({ label: org?.name, value: org?.id })) || []);
    },
    refetchOnWindowFocus: false,
  });

  // questions for selected org
  const { isLoading, isFetching, refetch } = useQuestionQuery({
    orgId: selectedOrgId,
    onSuccess: (data) => {
      setProgramList(data || []);
    },
  });

  // categories for selected org (used to join categoryName)
  const { isLoading: categoryLoading, isFetching: categoryFetching } = useQuestionCategoryQuery({
    orgId: selectedOrgId,
    onSuccess: (data) => setCategoryList(data || []),
  });

  // join questions with categoryName whenever data changes
  useEffect(() => {
    const joined = (programList || []).map((q) => {
      const categoryObj = categoryList.find((c) => c.id === q.category);
      return { ...q, categoryName: categoryObj ? categoryObj.categoryName : null };
    });
    setQuestionDataItem(joined);
    setFilterData(joined);
    setPage(0);
  }, [programList, categoryList]);

  const handleOrgChange = (opt) => {
    setSelectedOrgId(opt?.value || null); // queries refetch via key change
  };

  const handleFilterData = (data) => {
    setFilterData(data);
    setPage(0);
  };

  const handleShow = (data, isRO) => {
    setModalShow(modalShow ? false : data);
    setReadOnly(isRO);
  };

  const handleDelete = async (id, entryName) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName,
      actionBtnTitle: "Delete",
    });
    if (confirmed) {
      setDeleting(id);
      deleteQuestion(id)
        .then(() => {
          setFilterData((prev) => prev.filter((d) => d.id !== id));
          refetch();
          success("Question Deleted!");
        })
        .catch((res) => failed(res?.response?.data?.message || res?.response?.data?.error || res.message))
        .finally(() => setDeleting(false));
    }
  };

  const sortFilterData = (sort_by) => {
    setSortBy(sort_by);
    switch (sort_by) {
      case "AscName":
        setFilterData((prev) => [...prev].sort((a, b) => (a.question > b.question ? 1 : a.question < b.question ? -1 : 0)));
        break;
      case "DscName":
        setFilterData((prev) => [...prev].sort((a, b) => (a.question < b.question ? 1 : a.question > b.question ? -1 : 0)));
        break;
      default:
        break;
    }
  };

  return (
    <React.Fragment>
      <section className="common-listing">
        {modalShow ? <EditQuestion modalShow={modalShow} handleShow={handleShow} refreshTable={refetch} isReadOnly={readOnly} /> : null}

        <div className="heading-wrap h-change">
          <SearchBar page={page} setPage={setPage} list={questionDataItem} filterData={handleFilterData} />
          <div className="right-wrap ml-auto" style={{ margin: "left" }}>
            <Select
              options={organizations}
              isLoading={organizations.length <= 1}
              isDisabled={organizations.length <= 1}
              onChange={handleOrgChange}
              value={organizations.find((o) => o.value === selectedOrgId) || null}
              className="search-select"
              classNamePrefix="select"
              placeholder="Select Organization..."
            />
            <Button onClick={() => navigate("/app/create-questions")} className="custom-btn" style={{ width: "auto" }}>
              Create
            </Button>
          </div>
        </div>

        <div className="table-wrap">
          <Table responsive>
            <thead>
              <tr>
                {/* <th style={{ cursor: "pointer" }} onClick={() => sortFilterData(sortBy === "AscName" ? "DscName" : "AscName")}>
                  Question {sortBy === "AscName" ? <ArrowDropUp /> : <ArrowDropDown />}
                </th> */}
                <th>Question</th>
                <th>Category</th>
                <th>Answer Type</th>
                <th>Mandatory</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isFetching || categoryLoading || categoryFetching ? (
                [1, 2, 3, 4, 5, 6, 7].map((val) => (
                  <tr key={val}>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                    <td><Skeleton animation="wave" /></td>
                  </tr>
                ))
              ) : filterData?.length > 0 ? (
                filterData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((data) => {
                  const hasAccess = admin ? true : !!!data?.isDefault;
                  return (
                    <tr key={data?.id}>
                      <td>{data?.question}</td>
                      <td>{data?.categoryName}</td>
                      <td>
                        {data?.answerType === "freeText"
                          ? "Multi Line Text"
                          : data?.answerType === "singleFreeText"
                          ? "Single Line Text"
                          : data?.answerType === "dropDown"
                          ? "Dropdown"
                          : data?.answerType === "checkbox"
                          ? "Checkbox"
                          : data?.answerType === "radio"
                          ? "Radio"
                          : ""}
                      </td>
                      <td>{data?.required ? "Yes" : "No"}</td>
                      <td>
                        <div className="action-wrap">
                          {hasAccess ? (
                            <Tooltip title={"Edit"}>
                              <Link to="" variant="primary" onClick={() => handleShow(data, false)} className="success-btn">
                                <img src={EditIcon} alt="Edit" />
                              </Link>
                            </Tooltip>
                          ) : (
                            <Tooltip title={"View"}>
                              <Link to="" variant="primary" onClick={() => handleShow(data, true)} className="success-btn">
                                <img src={ViewIcon} alt="View" />
                              </Link>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <TableCell colSpan="5" style={{ textAlign: "center" }}>
                    No Data Available
                  </TableCell>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {filterData?.length > 0 ? (
          <CustomPagination
            tableData={filterData}
            totalRows={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            handlePage={(pageNo) => setPage(pageNo)}
          />
        ) : null}
      </section>
    </React.Fragment>
  );
}

export default QuestionsList;